import { filterAndSortPrototypes, type SortMode } from "./list-filter";
import { api, uploadFile, listGrants, createGrant, revokeGrant, setGrantDomains, Prototype, VersionInfo, Grant, ApiError, Viewer, getApiUrl } from "./api";
import { currentEmail, signOut } from "./auth";
import {
  renderCard, renderVersionRow, escape, VIEWER_BASE, type CardCtx,
  renderGrantsBand, GRANT_STALE_AFTER_DAYS, GRANT_MASKED_LINK, EYE_ICON_HIDDEN, EYE_ICON_SHOWN,
  COPY_ICON,
} from "./card";
import { deriveTagSuggestions } from "./tag-suggest";
import { mountTagInput, type TagInput } from "./tag-input";
import { transferBlockedReason, transferConsequences } from "./transfer";

function kindFor(file: File): "zip" | "html" {
  return /\.html?$/i.test(file.name) ? "html" : "zip";
}

/**
 * Make `zone` accept a file dragged from the desktop and route it into the
 * hidden `<input type=file>` that the upload handler already reads — so drop
 * and "Choose file" share one source of truth. A drop dispatches the input's
 * `change` event, so whatever the picker updates (the filename label) updates
 * on drop too. `dragover`/`drop` defaults are cancelled so the browser doesn't
 * navigate away to the dropped file. A depth counter keeps the highlight steady
 * as the pointer crosses child elements (each child fires its own enter/leave).
 */
export function wireDropZone(zone: HTMLElement, input: HTMLInputElement): void {
  let depth = 0;
  const highlight = (on: boolean) => zone.classList.toggle("dragover", on);
  const cancel = (e: Event) => { e.preventDefault(); e.stopPropagation(); };
  zone.addEventListener("dragenter", (e) => { cancel(e); depth++; highlight(true); });
  zone.addEventListener("dragover", cancel);
  zone.addEventListener("dragleave", (e) => { cancel(e); if (--depth <= 0) { depth = 0; highlight(false); } });
  zone.addEventListener("drop", (e) => {
    cancel(e);
    depth = 0;
    highlight(false);
    const file = (e as DragEvent).dataTransfer?.files?.[0];
    if (!file) return;
    const dt = new DataTransfer();
    dt.items.add(file);
    input.files = dt.files;
    input.dispatchEvent(new Event("change", { bubbles: true }));
  });
}

const SKELETON_CARDS = 3;
let filterMode: "all" | "mine" | string = "mine"; // "all", "mine", or an email address — default to the user's own shares
let allPrototypes: Prototype[] = [];
let maxRetainedVersions: number | undefined;
let suggestedTags: string[] = [];
let searchQuery = "";
let selectedTags: string[] = [];
let sortMode: SortMode = "newest";
let uploadTagInput: TagInput | null = null;
let currentViewer: Viewer | null = null;

function isAdminViewer(): boolean {
  return currentViewer?.isAdmin === true;
}

interface CardState { expanded: boolean; activating: boolean; }
const cardState = new Map<string, CardState>();
function stateFor(slug: string): CardState {
  let s = cardState.get(slug);
  if (!s) { s = { expanded: false, activating: false }; cardState.set(slug, s); }
  return s;
}

function skeletonListHtml(): string {
  const card = `
    <div class="skeleton-card" aria-hidden="true">
      <div class="skeleton-line skeleton-title"></div>
      <div class="skeleton-line skeleton-url"></div>
      <div class="skeleton-line skeleton-meta"></div>
    </div>
  `;
  return `
    <div class="skeleton-wrap" role="status" aria-label="Loading shares">
      ${card.repeat(SKELETON_CARDS)}
    </div>
  `;
}

// Material Design Icons paths (24x24 viewBox), MIT licensed.
// Inlined to avoid an icon-font dependency.
const HELP_ICON_PATH =
  "M11,18H13V16H11V18M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,20C7.59,20 4,16.41 4,12C4,7.59 7.59,4 12,4C16.41,4 20,7.59 20,12C20,16.41 16.41,20 12,20M12,6A4,4 0 0,0 8,10H10A2,2 0 0,1 12,8A2,2 0 0,1 14,10C14,12 11,11.75 11,15H13C13,12.75 16,12.5 16,10A4,4 0 0,0 12,6Z";
const LOGOUT_ICON_PATH =
  "M14.08,15.59L16.67,13H7V11H16.67L14.08,8.41L15.5,7L20.5,12L15.5,17L14.08,15.59M19,3A2,2 0 0,1 21,5V9.67L19,7.67V5H5V19H19V16.33L21,14.33V19A2,2 0 0,1 19,21H5C3.89,21 3,20.1 3,19V5C3,3.9 3.89,3 5,3H19Z";

function iconSvg(pathData: string, size = 18): string {
  return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="${pathData}"/></svg>`;
}

// Fills the fixed-height #vis-hint slot as the visibility choice changes —
// the min-height in styles.css is what keeps the form from reflowing, which
// is what makes describing all three options (rather than just picking a
// short label) affordable.
const VIS_HINTS: Record<string, string> = {
  internal:
    "Every Rewinder can open it once signed in. Nobody outside Rewind can, even with the URL.",
  restricted:
    "<b>You'll create share links after uploading</b>, from the Manage panel. Each gets a name so you can see who you sent it to, and can be revoked on its own. Links don't expire, so we'll ask you to keep this prototype permanently before the first one.",
  public:
    "<b>No sign-in at all.</b> The URL is unguessable, but treat it as public &mdash; once shared it can be forwarded or archived, and making it private later won't undo that.",
};

export function renderWho() {
  const el = document.getElementById("who");
  if (!el) return;

  const span = document.createElement("span");
  span.className = "who-email";
  span.textContent = `Signed in as ${currentEmail() ?? ""}`;

  // Help: anchor styled as an icon-only button. Anchor (not button) preserves
  // middle-click-to-open-in-new-tab and shows the URL in the status bar — both
  // useful affordances for nav.
  const helpLink = document.createElement("a");
  helpLink.href = "#help";
  helpLink.className = "icon-btn help-btn";
  helpLink.title = "Help";
  helpLink.setAttribute("aria-label", "Help");
  helpLink.innerHTML = iconSvg(HELP_ICON_PATH);

  // Sign out: real button (action, not navigation).
  const signOutBtn = document.createElement("button");
  signOutBtn.type = "button";
  signOutBtn.className = "btn-secondary signout-btn";
  signOutBtn.innerHTML = `${iconSvg(LOGOUT_ICON_PATH, 16)}<span>Sign out</span>`;
  signOutBtn.addEventListener("click", () => {
    signOut();
    window.location.reload();
  });

  el.replaceChildren(span, helpLink, signOutBtn);
}

export async function renderApp(
  root: HTMLElement,
  opts?: { maxRetainedVersions?: number; initialListPromise?: Promise<{ prototypes: Prototype[]; viewer?: Viewer }> },
) {
  maxRetainedVersions = opts?.maxRetainedVersions;
  root.innerHTML = `
    <section class="intro">
      <p>Upload and share AI-generated web content with your team — prototypes, dashboards, docs, anything HTML.</p>
      <p>Upload a single <strong>.html file</strong>, or a <strong>.zip</strong> containing everything your page needs (HTML, CSS, JS, images).
        For a zip, include an <code>index.html</code> at the root, or a single <code>.html</code> file that will be used as the entry point.</p>
      <p class="intro-note">Every share gets a viewer link. <strong>Internal</strong> shares require a Rewind Google sign-in to view; <strong>public</strong> shares are open to anyone with the URL.</p>
    </section>
    <section class="card">
      <h2>Upload</h2>
      <div class="upload-fields">
        <input type="text" id="title" placeholder="Title" />
        <input type="text" id="description" placeholder="Brief description (optional)" maxlength="200" />
        <fieldset class="upload-visibility">
          <legend>Visibility</legend>
          <label>
            <input type="radio" name="visibility" value="internal" checked>
            Internal only &mdash; anyone with a rewind.io/rewind.com Google account
          </label>
          <label>
            <input type="radio" name="visibility" value="restricted">
            Restricted &mdash; Rewinders, plus anyone you send a share link
          </label>
          <label>
            <input type="radio" name="visibility" value="public">
            Public link &mdash; anyone with the URL
          </label>
        </fieldset>
        <div class="vishint" id="vis-hint"></div>
        <div class="upload-tags-field">
          <label class="upload-tags-label">Tags (optional)</label>
          <div id="uploadTags" class="taginput"></div>
        </div>
        <div class="upload-row">
          <div class="dropzone" id="dropZone">
            <input type="file" id="file" accept=".zip,application/zip,.html,.htm,text/html" hidden />
            <button id="chooseFileBtn" class="btn-secondary" type="button">Choose file</button>
            <span id="fileName" class="file-name">No file chosen</span>
            <span class="dropzone-hint">or drag a file here</span>
          </div>
          <button id="uploadBtn" class="btn-primary">Upload</button>
        </div>
      </div>
      <div id="uploadStatus"></div>
    </section>
    <section class="card">
      <div class="list-header">
        <h2>Shares</h2>
        <div class="list-controls">
          <input type="search" id="listSearch" class="list-search" placeholder="Search title, description, owner, tag…" />
          <div class="filter-toggle">
            <button id="filterAll" class="filter-btn">All</button>
            <button id="filterMine" class="filter-btn active">Mine</button>
          </div>
          <select id="userFilter" class="user-filter" style="display:none">
            <option value="">All users</option>
          </select>
          <select id="sortMode" class="sort-select">
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
            <option value="title">Title A–Z</option>
            <option value="expiring">Expiring soon</option>
          </select>
          <button id="refreshBtn" class="refresh-btn" title="Refresh list">&#8635;</button>
        </div>
      </div>
      <div id="tagFilter" class="tag-filter"></div>
      <div id="list">${skeletonListHtml()}</div>
    </section>
  `;
  document.getElementById("uploadBtn")!.addEventListener("click", onUpload);
  const visHintEl = document.getElementById("vis-hint")!;
  const updateVisHint = () => {
    const val = (document.querySelector('input[name="visibility"]:checked') as HTMLInputElement | null)?.value ?? "internal";
    visHintEl.innerHTML = VIS_HINTS[val] ?? "";
  };
  for (const r of root.querySelectorAll<HTMLInputElement>('input[name="visibility"]')) {
    r.addEventListener("change", updateVisHint);
  }
  updateVisHint();
  document.getElementById("chooseFileBtn")!.addEventListener("click", () => {
    document.getElementById("file")!.click();
  });
  document.getElementById("file")!.addEventListener("change", () => {
    const file = (document.getElementById("file") as HTMLInputElement).files?.[0];
    document.getElementById("fileName")!.textContent = file ? file.name : "No file chosen";
  });
  wireDropZone(
    document.getElementById("dropZone") as HTMLElement,
    document.getElementById("file") as HTMLInputElement,
  );
  document.getElementById("filterAll")!.addEventListener("click", () => setFilter("all"));
  document.getElementById("filterMine")!.addEventListener("click", () => setFilter("mine"));
  document.getElementById("userFilter")!.addEventListener("change", onUserFilterChange);
  document.getElementById("refreshBtn")!.addEventListener("click", () => refreshList());
  const searchEl = document.getElementById("listSearch") as HTMLInputElement;
  let searchDebounce: ReturnType<typeof setTimeout> | undefined;
  searchEl.addEventListener("input", () => {
    clearTimeout(searchDebounce);
    searchDebounce = setTimeout(() => { searchQuery = searchEl.value; renderFilteredList(); }, 150);
  });
  document.getElementById("sortMode")!.addEventListener("change", (e) => {
    sortMode = (e.target as HTMLSelectElement).value as SortMode;
    renderFilteredList();
  });
  renderTagFilter();
  await refreshList(opts?.initialListPromise);
}

function setFilter(mode: "all" | "mine") {
  filterMode = mode;
  document.getElementById("filterAll")!.classList.toggle("active", mode === "all");
  document.getElementById("filterMine")!.classList.toggle("active", mode === "mine");
  const userSelect = document.getElementById("userFilter") as HTMLSelectElement;
  if (mode === "all") {
    userSelect.style.display = "";
    userSelect.value = "";
  } else {
    userSelect.style.display = "none";
  }
  renderFilteredList();
}

function onUserFilterChange() {
  const userSelect = document.getElementById("userFilter") as HTMLSelectElement;
  filterMode = userSelect.value || "all";
  renderFilteredList();
}

function populateUserFilter(prototypes: Prototype[]) {
  const select = document.getElementById("userFilter") as HTMLSelectElement;
  const owners = [...new Set(prototypes.map((p) => p.owner))].sort();
  const current = select.value;
  select.innerHTML = `<option value="">All users</option>`;
  for (const owner of owners) {
    const opt = document.createElement("option");
    opt.value = owner;
    opt.textContent = owner;
    select.appendChild(opt);
  }
  select.value = current;
  select.style.display = filterMode === "mine" ? "none" : "";
}

function renderTagFilter() {
  const el = document.getElementById("tagFilter");
  if (!el) return;
  if (suggestedTags.length === 0) { el.innerHTML = ""; return; }
  el.innerHTML = suggestedTags
    .map((t) => `<button class="tag-chip${selectedTags.includes(t) ? " tag-chip--active" : ""}" data-tag="${escape(t)}">${escape(t)}</button>`)
    .join("");
  for (const btn of el.querySelectorAll<HTMLButtonElement>(".tag-chip")) {
    btn.addEventListener("click", () => {
      const tag = btn.dataset.tag!;
      selectedTags = selectedTags.includes(tag) ? selectedTags.filter((x) => x !== tag) : [...selectedTags, tag];
      renderTagFilter();
      renderFilteredList();
    });
  }
}

function getFilteredPrototypes(): Prototype[] {
  return filterAndSortPrototypes(allPrototypes, {
    ownerFilter: filterMode,
    currentEmail: currentEmail(),
    search: searchQuery,
    selectedTags,
    sort: sortMode,
  });
}

async function onUpload() {
  const title = (document.getElementById("title") as HTMLInputElement).value.trim();
  const description = (document.getElementById("description") as HTMLInputElement).value.trim();
  const file = (document.getElementById("file") as HTMLInputElement).files?.[0];
  const status = document.getElementById("uploadStatus")!;
  if (!title) { status.textContent = "Title required"; return; }
  if (!file) { status.textContent = "Pick a .zip or .html/.htm file"; return; }

  const visibility =
    (document.querySelector('input[name="visibility"]:checked') as HTMLInputElement | null)?.value ??
    "internal";
  const tags = uploadTagInput ? uploadTagInput.getTags() : [];
  status.textContent = "Creating…";
  try {
    const presigned = await api.create(title, description || undefined, kindFor(file), visibility as "internal" | "restricted" | "public", tags);
    status.textContent = "Uploading…";
    await uploadFile(file, presigned);
    status.textContent = "Extracting…";
    const final = await pollUntilReady(presigned.slug);
    if (final.status === "ready") {
      const url = `${VIEWER_BASE}/${presigned.slug}/`;
      status.innerHTML = `Ready: <a href="${url}" target="_blank">${url}</a>`;
    } else {
      status.innerHTML = `<span class="error">${escape(final.error ?? "Failed")}</span>`;
    }
    (document.getElementById("title") as HTMLInputElement).value = "";
    (document.getElementById("description") as HTMLInputElement).value = "";
    (document.getElementById("file") as HTMLInputElement).value = "";
    document.getElementById("fileName")!.textContent = "No file chosen";
    uploadTagInput = null;
    await refreshList();
  } catch (e) {
    status.innerHTML = `<span class="error">${escape((e as Error).message)}</span>`;
  }
}

async function pollUntilReady(slug: string, version = 1): Promise<VersionInfo> {
  for (let i = 0; i < 60; i++) {
    const v = await api.getVersion(slug, version);
    if (v.status !== "pending") return v;
    await new Promise((r) => setTimeout(r, 2000));
  }
  throw new Error("Timed out waiting for extraction");
}

async function refreshList(preloaded?: Promise<{ prototypes: Prototype[]; viewer?: Viewer }>) {
  const el = document.getElementById("list")!;
  if (!preloaded) el.innerHTML = skeletonListHtml();
  try {
    const { prototypes, viewer } = await (preloaded ?? api.list());
    currentViewer = viewer ?? null;
    allPrototypes = prototypes;
    suggestedTags = deriveTagSuggestions(prototypes);
    populateUserFilter(prototypes);
    renderTagFilter();
    const uploadHost = document.getElementById("uploadTags");
    if (uploadHost) uploadTagInput = mountTagInput(uploadHost, uploadTagInput ? uploadTagInput.getTags() : [], suggestedTags);
    renderFilteredList();
  } catch (e) {
    el.innerHTML = `<div class="error">${escape((e as Error).message)}</div>`;
  }
}

function renderFilteredList() {
  const el = document.getElementById("list")!;
  const filtered = getFilteredPrototypes();
  if (filtered.length === 0) {
    const msg = filterMode === "mine" ? "You haven't uploaded any shares yet."
      : allPrototypes.length === 0 ? "No shares yet." : "No shares match this filter.";
    el.innerHTML = `<p>${msg}</p>`;
    return;
  }
  el.innerHTML = filtered.map((p) => renderCard(p, ctxFor(p))).join("");
  for (const p of filtered) wireCard(p);
}

function ctxFor(p: Prototype): CardCtx {
  const s = stateFor(p.slug);
  return { isMine: p.owner === currentEmail(), isAdmin: isAdminViewer(), expanded: s.expanded, activating: s.activating, maxRetainedVersions };
}

function wireCard(p: Prototype) {
  const root = document.querySelector<HTMLElement>(`.prototype[data-slug="${cssEsc(p.slug)}"]`);
  if (!root) return;
  const isMine = p.owner === currentEmail();
  document.getElementById(`manage-toggle-${p.slug}`)?.addEventListener("click", () => toggleManage(p));
  document.getElementById(`upload-new-${p.slug}`)?.addEventListener("click", () => {
    stateFor(p.slug).expanded = true;
    void updateCard(p.slug).then(() => document.getElementById(`version-choose-${p.slug}`)?.scrollIntoView({ block: "nearest" }));
  });
  // Only buttons that carry their own URL. A grants-band copy button has no
  // data-url (its URL lives on the row's .linkbox) and is wired by wireGrants,
  // so the selector keeps the two from binding the same button twice.
  for (const btn of root.querySelectorAll<HTMLButtonElement>(".copy-btn[data-url]")) {
    btn.addEventListener("click", () => copyWithFeedback(btn, btn.dataset.url ?? ""));
  }
  if (isMine) wireManage(p);
  // The owner row and the admin strip render the SAME control id
  // (transfer-${slug}), deliberately, and exactly one of the two renders per
  // card — so one listener here covers both the owner and the admin path.
  document.getElementById(`transfer-${p.slug}`)?.addEventListener("click", () => openTransferModal(p));
  // Load versions once here (covers both owners and non-owners) when the card is
  // expanded — wireManage only runs for owners, so the version list would
  // otherwise stay stuck on "Loading…" for non-owner expanded cards.
  if (stateFor(p.slug).expanded) {
    void loadVersions(p);
    // The grants band renders in TWO places: inside renderManagePanel for the
    // owner, and inside renderReadonlyManage for an admin (grants.ts admits
    // admins to list, create and revoke). Both are restricted-only —
    // renderGrantsBand returns "" otherwise. This gate must cover that whole
    // union: gated on ownership alone, an admin got a container that spins
    // forever — it renders, but the fetch that fills it never fires. The
    // failure mode is one-directional, because loadGrants itself returns early
    // when the container is absent: too narrow silently breaks the band, too
    // wide is merely a redundant check.
    if ((isMine || isAdminViewer()) && p.visibility === "restricted") void loadGrants(p);
  }
}

// No `root` parameter: every lookup here goes through document.getElementById,
// so the caller's element was never used. (wireCard genuinely needs its own.)
function wireManage(p: Prototype) {
  const on = (id: string, fn: () => void) => document.getElementById(`${id}-${p.slug}`)?.addEventListener("click", fn);
  on("del", () => onDelete(p.slug));
  on("keep", () => onKeep(p.slug));
  on("unkeep", () => onUnkeep(p.slug));
  on("make-internal", () => openMakeInternalModal(p));
  on("make-restricted", () => openMakeRestrictedModal(p));
  on("edit-tags", () => openEditTagsModal(p));
  on("edit-meta", () => openEditMetaModal(p));
  on("edit-meta-desc", () => openEditMetaModal(p));
  on("alias-save", () => onSetAlias(p.slug));
  on("alias-clear", () => onClearAlias(p.slug));
  on("alias-latest", () => onFollowLatest(p));
  on("alias-rename", () => onRenameAlias(p));
  on("gen-token", () => onGenerateToken(p.slug));
  document.getElementById(`version-choose-${p.slug}`)?.addEventListener("click", () => document.getElementById(`version-file-${p.slug}`)!.click());
  document.getElementById(`version-file-${p.slug}`)?.addEventListener("change", () => {
    const f = (document.getElementById(`version-file-${p.slug}`) as HTMLInputElement).files?.[0];
    document.getElementById(`version-filename-${p.slug}`)!.textContent = f ? f.name : "No file chosen";
  });
  document.getElementById(`version-upload-btn-${p.slug}`)?.addEventListener("click", () => onUploadVersion(p.slug));
  // (loadVersions is triggered once from wireCard, not here, to avoid a duplicate fetch.)
}

function cssEsc(slug: string): string { return slug.replace(/["\\]/g, "\\$&"); }

async function updateCard(slug: string): Promise<void> {
  const fresh = await api.get(slug).catch(() => allPrototypes.find((x) => x.slug === slug));
  if (!fresh) { renderFilteredList(); return; }
  const idx = allPrototypes.findIndex((x) => x.slug === slug);
  if (idx >= 0) allPrototypes[idx] = fresh;
  suggestedTags = deriveTagSuggestions(allPrototypes);
  renderTagFilter();
  const node = document.querySelector<HTMLElement>(`.prototype[data-slug="${cssEsc(slug)}"]`);
  if (!node) { renderFilteredList(); return; }
  const tmp = document.createElement("div");
  tmp.innerHTML = renderCard(fresh, ctxFor(fresh));
  const replacement = tmp.firstElementChild as HTMLElement;
  node.replaceWith(replacement);
  wireCard(fresh);
}

function toggleManage(p: Prototype) {
  const s = stateFor(p.slug);
  s.expanded = !s.expanded;
  void updateCard(p.slug);
}

async function loadVersions(p: Prototype) {
  const listEl = document.getElementById(`versions-${p.slug}`);
  if (!listEl) return;
  try {
    const { versions } = await api.listVersions(p.slug);
    const isMine = p.owner === currentEmail();
    listEl.innerHTML = versions.map((v) => renderVersionRow(p, v, isMine)).join("");
    for (const btn of listEl.querySelectorAll<HTMLElement>(".version-del-btn")) btn.addEventListener("click", () => onDeleteVersion(btn.dataset.slug!, parseInt(btn.dataset.version!, 10)));
    for (const btn of listEl.querySelectorAll<HTMLElement>(".pin-version")) btn.addEventListener("click", () => onPinVersion(btn.dataset.slug!, parseInt(btn.dataset.version!, 10)));
  } catch (e) {
    listEl.innerHTML = `<div class="error">${escape((e as Error).message)}</div>`;
  }
}

// Two band loads can legitimately be in flight for the same slug at once:
// updateCard re-renders the card and wireCard kicks off a load, while a
// user-triggered action (revoke, create) starts its own. They hit the same
// endpoint, so responses can arrive out of order and the LOSER would then
// overwrite the winner's HTML — leaving the band a version behind, and dropping
// the reveal that attemptCreateGrant applies to a freshly created link. A
// per-slug sequence number means only the newest request is allowed to paint.
const grantLoadSeq = new Map<string, number>();

async function loadGrants(p: Prototype): Promise<void> {
  const el = document.getElementById(`grants-${p.slug}`);
  if (!el) return;
  const seq = (grantLoadSeq.get(p.slug) ?? 0) + 1;
  grantLoadSeq.set(p.slug, seq);
  const isStale = () => grantLoadSeq.get(p.slug) !== seq;
  try {
    const grants = await listGrants(p.slug);
    if (isStale()) return;
    el.innerHTML = renderGrantsBand(p, grants, GRANT_STALE_AFTER_DAYS);
    wireGrants(p, el);
  } catch (e) {
    if (isStale()) return;
    el.innerHTML = `<div class="error">${escape((e as Error).message)}</div>`;
  }
}

// Exported as the seam tests/grants-band-dom.test.ts drives the band through.
// This is the real production entry point — loadGrants calls it immediately
// after writing renderGrantsBand's HTML — so a test that goes through it also
// covers the wiring itself: which selector gets which handler, and that a
// re-render rebinds. The previous arrangement exported the individual handler
// (onCopyGrantLink) purely so a test could call it directly, which bypassed
// exactly the part most likely to break.
export function wireGrants(p: Prototype, el: HTMLElement) {
  document.getElementById(`grant-new-${p.slug}`)?.addEventListener("click", () => onNewGrant(p));
  for (const row of el.querySelectorAll<HTMLElement>("tr[data-grant]")) {
    const id = row.dataset.grant!;
    row.querySelector<HTMLButtonElement>(".grant-eye")?.addEventListener("click", () => toggleGrantReveal(row));
    row.querySelector<HTMLButtonElement>(".grant-copy")?.addEventListener("click", (e) => onCopyGrantLink(row, e.currentTarget as HTMLButtonElement));
    // The row carries its own domain list in data-domains, so the dialog
    // pre-fills from what is on screen rather than from module state that a
    // re-rendered band could leave stale. Safe here in a way it would NOT be on
    // the verify page: the portal is owner-or-admin only, which is exactly the
    // authenticated surface the list is allowed to reach.
    row.querySelector<HTMLButtonElement>(".grant-domains")?.addEventListener("click", () =>
      openGrantDomainsModal(p, {
        id,
        label: "",
        createdBy: "",
        createdAt: "",
        allowedDomains: parseDomainInput(row.dataset.domains ?? ""),
      }),
    );
    row.querySelector<HTMLButtonElement>(".grant-revoke")?.addEventListener("click", () => onRevokeGrant(p, id));
  }
}

// The eye icon always reflects the CURRENT state (slashed while hidden, open
// while shown) — this is the only place link text/icon state changes, so
// both the click handler and the "reveal once on create" path below share it.
function toggleGrantReveal(row: HTMLElement) {
  const box = row.querySelector<HTMLElement>(".linkbox")!;
  const val = row.querySelector<HTMLElement>(".linkval")!;
  const btn = row.querySelector<HTMLButtonElement>(".grant-eye")!;
  const shown = box.classList.toggle("linkbox--shown");
  if (shown) {
    val.textContent = box.dataset.full ?? "";
    btn.innerHTML = EYE_ICON_SHOWN;
    btn.title = "Hide link";
    btn.setAttribute("aria-label", "Hide link");
  } else {
    val.textContent = GRANT_MASKED_LINK;
    btn.innerHTML = EYE_ICON_HIDDEN;
    btn.title = "Show link";
    btn.setAttribute("aria-label", "Show link");
  }
}

// Keyed on the button element rather than held in a closure, so a re-rendered
// grants band (loadGrants replaces the whole innerHTML) starts clean instead of
// inheriting a stale label from a button that no longer exists.
const copyLabels = new WeakMap<HTMLButtonElement, string>();
const copyTimers = new WeakMap<HTMLButtonElement, ReturnType<typeof setTimeout>>();
const COPY_FEEDBACK_MS = 1500;
export const COPY_DONE = "✓";

// The ONE copy behaviour behind every .copy-btn — main link, friendly URL,
// upload token, and each restricted-access-link row. It used to be two: the
// card's inline handler and a separate, more careful one for grants. That is how
// the same card ended up with two different copy affordances, and it also meant
// only one of them had the double-click fix below.
//
// The glyph on screen is captured ONCE and any pending restore is cancelled
// before a new one is scheduled. Re-reading btn.textContent on every click meant
// a second click inside the feedback window captured the tick as the
// "original", so both timers restored that and the button showed a tick
// permanently. The copy itself always worked; only the glyph was wrong.
function copyWithFeedback(btn: HTMLButtonElement, text: string) {
  const restore = () => {
    btn.textContent = copyLabels.get(btn) ?? "";
    copyTimers.delete(btn);
  };

  const pending = copyTimers.get(btn);
  if (pending !== undefined) {
    clearTimeout(pending);
  } else {
    // No restore in flight, so what is on screen now IS the real label.
    copyLabels.set(btn, btn.textContent ?? "");
  }

  btn.textContent = COPY_DONE;
  copyTimers.set(btn, setTimeout(restore, COPY_FEEDBACK_MS));

  // writeText rejects when clipboard permission is denied or the document isn't
  // focused. Left unhandled that is an unhandled rejection in the console (and a
  // failure the day unhandled rejections are fatal in tests); handled, it also
  // lets us take the tick back — claiming we copied a share link we didn't is
  // worse than showing nothing, since the user would send whatever was on the
  // clipboard before. Promise.resolve wraps it because the DOM stub in tests
  // (and any polyfill) may return a non-promise.
  Promise.resolve(navigator.clipboard.writeText(text)).catch(() => {
    const scheduled = copyTimers.get(btn);
    if (scheduled !== undefined) clearTimeout(scheduled);
    restore();
  });
}

// Copies the full URL WITHOUT revealing it in the field — re-sending an
// existing link is the common case, so Copy must not force a reveal first. The
// URL is read from the row's .linkbox, the single place it lives in the DOM.
function onCopyGrantLink(row: HTMLElement, btn: HTMLButtonElement) {
  const box = row.querySelector<HTMLElement>(".linkbox")!;
  copyWithFeedback(btn, box.dataset.full ?? "");
}

async function onRevokeGrant(p: Prototype, id: string) {
  if (!confirm("Revoke this link? Anyone using it loses access within a few seconds.")) return;
  try {
    await revokeGrant(p.slug, id);
  } catch (e) {
    // 404 here means the grant is already gone — revoked elsewhere, or never
    // belonged to this slug. Not a scary error: just refresh the list.
    if ((e as Error).message !== "not found") { alert((e as Error).message); return; }
  }
  await loadGrants(p);
}

// Comma, whitespace or newline separated, lowercased so what the owner sees
// after saving matches what the server stored. Kept a small exported pure
// function so the DOM test can target it directly rather than through a dialog.
// Deliberately does NOT validate shape — the server owns that, and duplicating
// the rule here would drift.
export function parseDomainInput(raw: string): string[] {
  return raw
    .split(/[\s,]+/)
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
}

// Shared chrome for the two domain dialogs, following the .modal-overlay
// pattern used by the revoke and keep-first modals.
function openModal(
  titleHtml: string,
  bodyHtml: string,
  confirmLabel: string,
  onConfirm: (overlay: HTMLElement, fail: (msg: string) => void) => Promise<void> | void,
): HTMLElement {
  const overlay = document.createElement("div");
  overlay.className = "modal-overlay";
  overlay.innerHTML = `
    <div class="modal" role="dialog" aria-labelledby="gd-title">
      <div class="modal-content" style="padding: 24px;">
        <h2 id="gd-title" style="margin: 0 0 12px; font-size: 1rem;">${titleHtml}</h2>
        ${bodyHtml}
        <p class="modal-error" role="alert"></p>
        <div class="modal-actions">
          <button class="btn-secondary btn-cancel">Cancel</button>
          <button class="btn-primary btn-confirm">${confirmLabel}</button>
        </div>
      </div>
    </div>`;
  document.body.appendChild(overlay);
  const close = () => overlay.remove();
  const fail = (msg: string) => {
    overlay.querySelector<HTMLElement>(".modal-error")!.textContent = msg;
    const btn = overlay.querySelector<HTMLButtonElement>(".btn-confirm")!;
    btn.disabled = false;
    btn.textContent = confirmLabel;
  };
  overlay.querySelector(".btn-cancel")!.addEventListener("click", close);
  overlay.addEventListener("click", (e) => { if (e.target === overlay) close(); });
  overlay.querySelector(".btn-confirm")!.addEventListener("click", async () => {
    overlay.querySelector<HTMLElement>(".modal-error")!.textContent = "";
    await onConfirm(overlay, fail);
  });
  return overlay;
}

// The copy is specified rather than left open because the failure mode here is
// social: "Recipients must confirm they have an address at one of these
// domains" is true, while "only your partner can open this" overstates it.
//
// Examples stay GENERIC on purpose. Placeholder text is product UI, visible to
// everyone who opens the dialog, so a real customer's domain here would leak
// who we work with — and a real person's name alongside it would say who we
// deal with there. Use acme.com / example.com, never a live account.
const DOMAINS_HELP = `
  <p style="margin: 8px 0; font-size: 0.875rem; line-height: 1.5; color: var(--loop-neutral-800);">
    Recipients must confirm they have an address at one of these domains: we email
    them a six-digit code and they enter it before the link opens. Leave empty for
    no check &mdash; anyone with the link gets in.
  </p>
  <p style="margin: 8px 0; font-size: 0.8125rem; line-height: 1.5; color: var(--loop-neutral-800);">
    Exact domains only, separated by commas. <code>acme.com</code> does not
    cover <code>mail.acme.com</code> &mdash; add each one.
  </p>`;

// A CHECKBOX, not an optional text field. Verification is opt-in and off is the
// pre-existing behaviour, but a bare input beside a paragraph of explanation
// reads as something that must be filled in — which would push owners into
// gating links that never needed it. An unticked box says "off" unambiguously,
// and it lets the explanation stay hidden until it is relevant.
//
// The box reflects the CURRENT state when editing: ticked iff the link already
// has domains.
function domainsField(value: string): string {
  const enabled = value.trim().length > 0;
  return `
    <label style="display:flex; align-items:flex-start; gap:8px; margin:16px 0 0; font-size:0.875rem; font-weight:600; cursor:pointer;">
      <input type="checkbox" class="domains-toggle" style="width:auto; margin:2px 0 0;" ${enabled ? "checked" : ""}>
      <span>Require recipients to verify their email address</span>
    </label>
    <div class="domains-wrap"${enabled ? "" : " hidden"}>
      ${DOMAINS_HELP}
      <label for="gd-domains" style="display:block; margin: 4px 0; font-size: 0.8125rem; font-weight: 600;">Allowed email domains</label>
      <input id="gd-domains" class="domains-input" type="text" style="width:100%; box-sizing:border-box;"
             placeholder="acme.com, example.com" value="${escape(value)}">
    </div>`;
}

// Reveals the domain list only while the box is ticked. Focus moves into the
// input on tick so the next keystroke lands where the user is looking.
function wireDomainsToggle(overlay: HTMLElement): void {
  const toggle = overlay.querySelector<HTMLInputElement>(".domains-toggle");
  const wrap = overlay.querySelector<HTMLElement>(".domains-wrap");
  if (!toggle || !wrap) return;
  toggle.addEventListener("change", () => {
    wrap.hidden = !toggle.checked;
    if (toggle.checked) overlay.querySelector<HTMLInputElement>(".domains-input")?.focus();
  });
}

// The box is the source of truth, not the text field: unticking sends [] even
// if the input still holds text, so "turn this off" cannot be defeated by
// leftover content the user never cleared.
function readDomains(overlay: HTMLElement): { ok: true; domains: string[] } | { ok: false; error: string } {
  const toggle = overlay.querySelector<HTMLInputElement>(".domains-toggle");
  if (toggle && !toggle.checked) return { ok: true, domains: [] };
  const domains = parseDomainInput(
    overlay.querySelector<HTMLInputElement>(".domains-input")?.value ?? "",
  );
  if (domains.length === 0) {
    return {
      ok: false,
      error: "Add at least one domain, or untick email verification to let anyone with the link in.",
    };
  }
  return { ok: true, domains };
}

export function openGrantDomainsModal(p: Prototype, g: Grant): HTMLElement {
  const overlay = openModal(
    "Email domains for this link",
    `${domainsField((g.allowedDomains ?? []).join(", "))}
     <p style="margin: 12px 0 0; font-size: 0.8125rem; line-height: 1.5; color: var(--loop-neutral-800);">
       Changing this does not sign out anyone who has <b>already verified</b> &mdash; they keep
       access for up to seven days. To cut access off now, <b>revoke</b> the link instead;
       that takes effect within a few seconds.
     </p>`,
    "Save",
    async (ov, fail) => {
      const read = readDomains(ov);
      if (!read.ok) {
        fail(read.error);
        return;
      }
      const btn = ov.querySelector<HTMLButtonElement>(".btn-confirm")!;
      btn.disabled = true;
      btn.textContent = "Saving…";
      try {
        await setGrantDomains(p.slug, g.id, read.domains);
      } catch (e) {
        fail((e as Error).message);
        return;
      }
      ov.remove();
      await loadGrants(p);
    },
  );
  wireDomainsToggle(overlay);
  return overlay;
}

export function openNewGrantModal(p: Prototype): HTMLElement {
  const overlay = openModal(
    "New share link",
    `<label for="gd-label" style="display:block; margin: 4px 0; font-size: 0.8125rem; font-weight: 600;">Link name</label>
     <input id="gd-label" class="label-input" type="text" style="width:100%; box-sizing:border-box;"
            placeholder="e.g. Acme Corp &mdash; design review">
     ${domainsField("")}`,
    "Create link",
    async (ov, fail) => {
      const label = ov.querySelector<HTMLInputElement>(".label-input")!.value.trim();
      if (!label) {
        fail("Give the link a name so you can tell it apart later.");
        return;
      }
      const read = readDomains(ov);
      if (!read.ok) {
        fail(read.error);
        return;
      }
      ov.remove();
      await attemptCreateGrant(p, label, read.domains);
    },
  );
  wireDomainsToggle(overlay);
  return overlay;
}

function onNewGrant(p: Prototype) {
  openNewGrantModal(p);
}

// Split out from onNewGrant so the "Keep permanently" remedy in the
// prototype-expires dialog can retry the SAME create call (same label)
// once the prototype has been kept, without re-prompting the user.
async function attemptCreateGrant(
  p: Prototype,
  label: string,
  allowedDomains: string[] = [],
): Promise<void> {
  try {
    const created = await createGrant(p.slug, label, allowedDomains);
    await loadGrants(p);
    // Show the new link revealed once so it can be copied immediately —
    // re-issuing should be a single click, not click-then-hunt-for-the-eye.
    const el = document.getElementById(`grants-${p.slug}`);
    const row = el?.querySelector<HTMLElement>(`tr[data-grant="${cssEsc(created.id)}"]`);
    if (row) toggleGrantReveal(row);
  } catch (e) {
    const err = e as ApiError;
    if (err.status === 409 && err.body?.error === "prototype expires" && typeof err.body.expiresAt === "number") {
      openKeepFirstModal(p, err.body.expiresAt as number, () =>
        attemptCreateGrant(p, label, allowedDomains),
      );
      return;
    }
    alert(err.message);
  }
}

// 409 "prototype expires" on grant create: access links never expire, but the
// prototype itself does (and ttl-cleanup deletes its files), so the remedy is
// to keep it permanently first, then retry the SAME grant create.
export function openKeepFirstModal(p: Prototype, expiresAt: number, retry: () => Promise<void>): void {
  const overlay = document.createElement("div");
  overlay.className = "modal-overlay";
  const daysLeft = Math.max(0, Math.ceil((expiresAt * 1000 - Date.now()) / 86400000));
  overlay.innerHTML = `
    <div class="modal keep-first-modal" role="dialog" aria-labelledby="kf-title">
      <div class="modal-content" style="padding: 24px;">
        <h2 id="kf-title" style="margin: 0 0 12px; font-size: 1rem;">Keep this prototype first</h2>
        <p style="margin: 8px 0; font-size: 0.875rem; line-height: 1.5; color: var(--loop-neutral-800);">
          Access links don't expire, but "${escape(p.title)}" currently does &mdash; in ${daysLeft} day${daysLeft === 1 ? "" : "s"}.
          Once it expires, its files are deleted and any link you create now would break for good.
        </p>
        <p style="margin: 8px 0; font-size: 0.875rem; line-height: 1.5; color: var(--loop-neutral-800);">
          Keep it permanently, then the link will be created right away. Revoking a link later takes effect <b>within a few seconds</b>, and re-issuing a new one is <b>one click</b> away in the Manage panel &mdash; so there's no need to be sparing with links once it's kept.
        </p>
        <div class="modal-actions">
          <button class="btn-secondary btn-cancel">Cancel</button>
          <button class="btn-primary btn-confirm">Keep permanently</button>
        </div>
      </div>
    </div>`;
  document.body.appendChild(overlay);
  const close = () => overlay.remove();
  overlay.querySelector(".btn-cancel")!.addEventListener("click", close);
  overlay.addEventListener("click", (e) => { if (e.target === overlay) close(); });
  overlay.querySelector(".btn-confirm")!.addEventListener("click", async () => {
    const btn = overlay.querySelector<HTMLButtonElement>(".btn-confirm")!;
    btn.disabled = true;
    btn.textContent = "Keeping…";
    try {
      await api.keep(p.slug);
      close();
      // updateCard BEFORE the retry, and not just loadGrants. Keeping the
      // prototype changes its Retention row, and the retry only refreshes the
      // access-links band — so the Settings panel went on claiming the
      // prototype still expires (with the old date) until something else
      // refreshed the card. The order matters: updateCard re-renders the card,
      // which would discard the reveal the retry applies to the new link, and
      // loadGrants' sequence guard makes the card's own band load lose to the
      // retry's rather than race it.
      await updateCard(p.slug);
      await retry();
    } catch (err) {
      btn.disabled = false;
      btn.textContent = "Keep permanently";
      alert(`Failed: ${(err as Error).message}`);
    }
  });
}

async function onSetAlias(slug: string) {
  const input = document.getElementById(`alias-name-${slug}`) as HTMLInputElement | null;
  const name = input?.value.trim() ?? "";
  if (!name) return;
  try { await api.setAlias(slug, { name, mode: "latest" }); await beginAliasActivation(slug); }
  catch (e) { alert((e as Error).message); }
}
async function onRenameAlias(p: Prototype) {
  const name = prompt("New friendly URL name:", p.friendlyName ?? "")?.trim();
  if (!name || name === p.friendlyName) return;
  try { await api.setAlias(p.slug, { name, mode: p.aliasMode ?? "latest", ...(p.aliasMode === "pinned" && p.pinnedVersion ? { version: p.pinnedVersion } : {}) }); await beginAliasActivation(p.slug); }
  catch (e) { alert((e as Error).message); }
}
async function beginAliasActivation(slug: string) {
  stateFor(slug).activating = true;
  await updateCard(slug);
  const deadline = Date.now() + 90_000;
  const tick = async () => {
    if (!stateFor(slug).activating) return;
    try { const { ready } = await api.verifyAlias(slug); if (ready) { stateFor(slug).activating = false; await updateCard(slug); return; } } catch { /* keep polling */ }
    if (Date.now() >= deadline) { stateFor(slug).activating = false; await updateCard(slug); return; }
    window.setTimeout(tick, 3000);
  };
  window.setTimeout(tick, 3000);
}
async function onClearAlias(slug: string) {
  if (!confirm("Remove the friendly URL? The shareable link will stop working.")) return;
  try { await api.clearAlias(slug); stateFor(slug).activating = false; await updateCard(slug); }
  catch (e) { alert((e as Error).message); }
}
async function onFollowLatest(p: Prototype) {
  if (!p.friendlyName) return;
  try { await api.setAlias(p.slug, { name: p.friendlyName, mode: "latest" }); await updateCard(p.slug); }
  catch (e) { alert((e as Error).message); }
}
async function onPinVersion(slug: string, version: number) {
  const proto = allPrototypes.find((x) => x.slug === slug);
  if (!proto?.friendlyName) { alert("Set a friendly URL first, then pin a version."); return; }
  try { await api.setAlias(slug, { name: proto.friendlyName, mode: "pinned", version }); await updateCard(slug); }
  catch (e) { alert((e as Error).message); }
}
async function onKeep(slug: string) { try { await api.keep(slug); await updateCard(slug); } catch (e) { alert((e as Error).message); } }
// "Let it expire" was the one destructive settings action with no confirmation
// at all — one click re-armed a 90-day fuse, and on a restricted prototype that
// fuse sits under every live share link (ttl-cleanup deletes the objects). It
// now confirms, and surfaces unkeep.ts's "grants exist" 409 with the same
// revoke-first remedy attemptMakeInternal gives for the identical shape.
async function onUnkeep(slug: string) {
  const p = allPrototypes.find((x) => x.slug === slug);
  const restricted = p?.visibility === "restricted";
  const warning = restricted
    ? "Let this expire? It will be deleted in 90 days, and every share link you've handed out dies with it. You'll be asked to revoke the links first."
    : "Let this expire? It will be deleted in 90 days and the viewer URL will stop working.";
  if (!confirm(warning)) return;
  try {
    await api.unkeep(slug);
    await updateCard(slug);
  } catch (e) {
    const err = e as ApiError;
    if (err.status === 409 && err.body?.error === "grants exist" && typeof err.body.count === "number" && p) {
      await openRevokeGrantsBeforeExpiryModal(p, err.body.count as number);
      return;
    }
    alert(err.message);
  }
}
async function onDelete(slug: string) {
  if (!confirm("Delete this share? The URL will stop working.")) return;
  try {
    await api.del(slug);
    allPrototypes = allPrototypes.filter((x) => x.slug !== slug);
    cardState.delete(slug);
    document.querySelector(`.prototype[data-slug="${cssEsc(slug)}"]`)?.remove();
    if (allPrototypes.length === 0) renderFilteredList();
  } catch (e) { alert((e as Error).message); }
}
async function onUploadVersion(slug: string) {
  const file = (document.getElementById(`version-file-${slug}`) as HTMLInputElement).files?.[0];
  const note = (document.getElementById(`version-note-${slug}`) as HTMLInputElement).value.trim();
  const status = document.getElementById(`version-status-${slug}`)!;
  if (!file) { status.textContent = "Pick a .zip or .html/.htm file"; return; }
  status.textContent = "Creating version…";
  try {
    const presigned = await api.createVersion(slug, note || undefined, undefined, kindFor(file));
    status.textContent = "Uploading…";
    await uploadFile(file, presigned);
    status.textContent = "Extracting…";
    const final = await pollUntilReady(slug, presigned.version);
    const msg = final.status === "ready"
      ? `<span class="success">v${presigned.version} ready</span>`
      : `<span class="error">${escape(final.error ?? "Failed")}</span>`;
    await updateCard(slug);
    document.getElementById(`version-status-${slug}`)?.insertAdjacentHTML("beforeend", msg);
  } catch (e) { status.innerHTML = `<span class="error">${escape((e as Error).message)}</span>`; }
}
async function onDeleteVersion(slug: string, version: number) {
  if (!confirm(`Delete version ${version}? This cannot be undone.`)) return;
  try { await api.deleteVersion(slug, version); await updateCard(slug); }
  catch (e) { alert((e as Error).message); }
}

async function onGenerateToken(slug: string) {
  const display = document.getElementById(`token-display-${slug}`)!;
  try {
    const { token, expiresAt } = await api.generateToken(slug);
    const expiresIn = Math.floor((expiresAt - Date.now() / 1000) / 3600);
    const apiEndpoint = getApiUrl();

    display.innerHTML = `
      <div class="token-info">
        <div class="token-value">
          <code id="token-code-${slug}">${escape(token)}</code>
          <button class="copy-btn" id="copy-token-${slug}" title="Copy token" aria-label="Copy token">${COPY_ICON}</button>
        </div>
        <div class="token-expiry">Expires in ~${expiresIn}h</div>
        <details class="token-usage">
          <summary>Usage instructions</summary>
          <pre>
# 1. Get presigned upload URL
curl -X POST \\
  -H "Authorization: ProtoToken ${escape(token)}" \\
  -H "Content-Type: application/json" \\
  -d '{"note": "your change note"}' \\
  ${escape(apiEndpoint)}/prototypes/${escape(slug)}/versions

# 2. Upload ZIP to the returned URL
curl -X POST &lt;uploadUrl&gt; -F "key=..." -F "file=@prototype.zip"
          </pre>
        </details>
        <button class="btn-secondary btn-sm" id="regen-token-${slug}">Regenerate</button>
      </div>
    `;
    const copyToken = document.getElementById(`copy-token-${slug}`) as HTMLButtonElement | null;
    copyToken?.addEventListener("click", () => copyWithFeedback(copyToken, token));
    document.getElementById(`regen-token-${slug}`)?.addEventListener("click", () => onGenerateToken(slug));
  } catch (e) {
    display.innerHTML = `<span class="error">${escape((e as Error).message)}</span>`;
  }
}

function openEditTagsModal(p: Prototype) {
  const overlay = document.createElement("div");
  overlay.className = "modal-overlay";
  overlay.innerHTML = `
    <div class="modal edit-tags-modal" role="dialog" aria-labelledby="et-title">
      <div class="modal-content" style="padding:24px;">
        <h2 id="et-title" style="margin:0 0 12px; font-size:1rem;">Edit tags — ${escape(p.title)}</h2>
        <div id="et-input" class="taginput"></div>
        <div class="modal-actions"><button class="btn-secondary btn-cancel">Cancel</button><button class="btn-primary btn-confirm">Save</button></div>
      </div>
    </div>`;
  document.body.appendChild(overlay);
  const ti = mountTagInput(overlay.querySelector<HTMLElement>("#et-input")!, p.tags ?? [], suggestedTags);
  const close = () => overlay.remove();
  overlay.querySelector(".btn-cancel")!.addEventListener("click", close);
  overlay.addEventListener("click", (e) => { if (e.target === overlay) close(); });
  overlay.querySelector(".btn-confirm")!.addEventListener("click", async () => {
    const btn = overlay.querySelector<HTMLButtonElement>(".btn-confirm")!;
    btn.disabled = true; btn.textContent = "Saving…";
    try { await api.patch(p.slug, { tags: ti.getTags() }); close(); await updateCard(p.slug); }
    catch (err) { btn.disabled = false; btn.textContent = "Save"; alert(`Failed: ${(err as Error).message}`); }
  });
}

function openEditMetaModal(p: Prototype) {
  const overlay = document.createElement("div");
  overlay.className = "modal-overlay";
  overlay.innerHTML = `
    <div class="modal" role="dialog" aria-labelledby="em-title">
      <div class="modal-content" style="padding:24px;">
        <h2 id="em-title" style="margin:0 0 12px; font-size:1rem;">Edit details</h2>
        <label class="flabel">Title</label>
        <input id="em-title-input" class="meta-input" maxlength="200" value="${escape(p.title)}" />
        <label class="flabel" style="margin-top:10px">Description</label>
        <input id="em-desc-input" class="meta-input" maxlength="200" value="${escape(p.description ?? "")}" placeholder="Brief description (optional)" />
        <div class="modal-actions"><button class="btn-secondary btn-cancel">Cancel</button><button class="btn-primary btn-confirm">Save</button></div>
      </div>
    </div>`;
  document.body.appendChild(overlay);
  const close = () => overlay.remove();
  overlay.querySelector(".btn-cancel")!.addEventListener("click", close);
  overlay.addEventListener("click", (e) => { if (e.target === overlay) close(); });
  overlay.querySelector(".btn-confirm")!.addEventListener("click", async () => {
    const title = (overlay.querySelector("#em-title-input") as HTMLInputElement).value.trim();
    const description = (overlay.querySelector("#em-desc-input") as HTMLInputElement).value.trim();
    if (!title) { alert("Title is required"); return; }
    const btn = overlay.querySelector<HTMLButtonElement>(".btn-confirm")!;
    btn.disabled = true; btn.textContent = "Saving…";
    try { await api.patch(p.slug, { title, description }); close(); await updateCard(p.slug); }
    catch (err) { btn.disabled = false; btn.textContent = "Save"; alert(`Failed: ${(err as Error).message}`); }
  });
}

// Hands the prototype to a different Rewinder. The copy has to carry two
// facts at once: every live /s/<id>/ link keeps resolving (transfer.ts writes
// no KVS, no S3, no grant), while the upload token — a credential, not a
// link — is revoked in the same write. The consequence list's grant count
// comes from a live fetch, never from a cache: CardState holds only
// {expanded, activating}, the same reasoning as openRevokeGrantsFirstModal. A
// failed fetch degrades to omitting the links row, which is honest rather
// than guessing.
export async function openTransferModal(p: Prototype): Promise<void> {
  let grantCount = 0;
  if (p.visibility === "restricted") {
    try {
      grantCount = (await listGrants(p.slug)).length;
    } catch {
      grantCount = 0;
    }
  }
  const overlay = document.createElement("div");
  overlay.className = "modal-overlay";
  const rows = transferConsequences(grantCount, p.hasToken === true)
    .map((c) => `
      <div class="conseq-row conseq-${c.kind === "keep" ? "keep" : "warn"}">
        <span>${escape(c.text)}</span>
      </div>`)
    .join("");
  overlay.innerHTML = `
    <div class="modal" role="dialog" aria-labelledby="tf-title">
      <div class="modal-content" style="padding:24px;">
        <h2 id="tf-title" style="margin:0 0 12px; font-size:1rem;">Transfer &ldquo;${escape(p.title)}&rdquo;</h2>
        <p style="margin:8px 0; font-size:0.875rem; line-height:1.5; color:var(--loop-neutral-800);">
          Ownership moves to the person you name. They get the Manage panel &mdash; versions,
          visibility, retention and access links &mdash; and you lose it.
        </p>
        <label class="flabel" for="tf-owner">New owner</label>
        <input type="text" id="tf-owner" class="meta-input" placeholder="name@rewind.io"
          aria-describedby="tf-hint tf-err" />
        <div class="tf-hint" id="tf-hint">Must be a Rewind address.</div>
        <div class="conseq">${rows}</div>
        <!-- role="alert" so a refusal is announced when it appears, not only seen.
             The element is present from the start (empty, display:none) rather than
             injected on failure, because a live region has to exist before its
             content changes for assistive tech to notice the change at all. -->
        <div class="tf-err" id="tf-err" role="alert" style="display:none"></div>
        <div class="modal-actions">
          <button class="btn-secondary btn-cancel">Cancel</button>
          <button class="btn-primary btn-confirm">Transfer</button>
        </div>
      </div>
    </div>`;
  document.body.appendChild(overlay);
  const close = () => overlay.remove();
  const input = overlay.querySelector<HTMLInputElement>("#tf-owner")!;
  const err = overlay.querySelector<HTMLElement>("#tf-err")!;
  overlay.querySelector(".btn-cancel")!.addEventListener("click", close);
  overlay.addEventListener("click", (e) => { if (e.target === overlay) close(); });
  // One place that shows a refusal, so aria-invalid and the live region can
  // never drift apart from each other or from the visible state.
  const showErr = (msg: string) => {
    err.textContent = msg;
    err.style.display = "";
    input.setAttribute("aria-invalid", "true");
  };
  overlay.querySelector(".btn-confirm")!.addEventListener("click", async () => {
    const blocked = transferBlockedReason(input.value, p.owner);
    if (blocked) {
      showErr(blocked);
      return;
    }
    // Clear the previous refusal before attempting again, so a retry that gets
    // further does not leave the input still marked invalid.
    input.removeAttribute("aria-invalid");
    const btn = overlay.querySelector<HTMLButtonElement>(".btn-confirm")!;
    btn.disabled = true;
    btn.textContent = "Transferring…";
    try {
      const res = await api.transferPrototype(p.slug, input.value.trim());
      close();
      alert(
        `Transferred to ${res.owner}.\n` +
        `${res.grantCount} access link${res.grantCount === 1 ? "" : "s"} still live.` +
        (res.tokenRevoked ? "\nThe upload token was revoked." : ""),
      );
      await refreshList();
    } catch (e) {
      btn.disabled = false;
      btn.textContent = "Transfer";
      showErr((e as Error).message);
    }
  });
}

// INTERNAL -> RESTRICTED. Wired only from the internal row (card.ts), because
// that is the only origin patch.ts accepts for this target: public ->
// restricted is refused as phase 2, and restricted is already there.
//
// The copy has to set the expectation that this alone changes nothing an
// outsider can see — a restricted prototype with no grants is exactly as
// closed as an internal one, and the sharing happens in the access-links band
// that appears afterwards. Saying so up front is what stops the owner
// believing they have just published something.
function openMakeRestrictedModal(p: Prototype) {
  const overlay = document.createElement("div");
  overlay.className = "modal-overlay";
  overlay.innerHTML = `
    <div class="modal make-restricted-modal" role="dialog" aria-labelledby="mr-title">
      <div class="modal-content" style="padding: 24px;">
        <h2 id="mr-title" style="margin: 0 0 12px; font-size: 1rem;">Make "${escape(p.title)}" restricted?</h2>
        <p style="margin: 8px 0; font-size: 0.875rem; line-height: 1.5; color: var(--loop-neutral-800);">
          Nothing becomes visible to anyone outside Rewind by doing this. It stays exactly as closed as it is now &mdash; sign-in required, Rewinders only &mdash; until <b>you</b> create a share link for a specific person.
        </p>
        <p style="margin: 8px 0; font-size: 0.875rem; line-height: 1.5; color: var(--loop-neutral-800);">
          Once it's restricted, a <b>Restricted access links</b> panel appears here and you create named links from it, one per recipient. Each link is permanent until you revoke it. Revoking takes effect <b>within a few seconds</b>, and re-issuing is <b>one click</b>.
        </p>
        <div class="modal-actions">
          <button class="btn-secondary btn-cancel">Cancel</button>
          <button class="btn-primary btn-confirm">Make restricted</button>
        </div>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  const close = () => overlay.remove();
  overlay.querySelector(".btn-cancel")!.addEventListener("click", close);
  overlay.addEventListener("click", (e) => { if (e.target === overlay) close(); });

  overlay.querySelector(".btn-confirm")!.addEventListener("click", async () => {
    const btn = overlay.querySelector<HTMLButtonElement>(".btn-confirm")!;
    btn.disabled = true;
    btn.textContent = "Working…";
    try {
      // `visibility`, never `isInternal` — the legacy boolean cannot express
      // restricted at all, and this transition moves no S3 objects (both
      // internal and restricted content already live under p/__internal/).
      await api.patch(p.slug, { visibility: "restricted" });
      close();
      // Re-render so the access-links band the copy just promised is actually
      // there; wireCard's restricted-only branch then loads it.
      await updateCard(p.slug);
    } catch (e) {
      btn.disabled = false;
      btn.textContent = "Make restricted";
      alert(`Failed: ${(e as Error).message}`);
    }
  });
}

// Making a prototype internal-only has two distinct origins with two
// distinct payloads (Ruling 3, task 10): a PUBLIC origin must send
// `isInternal: true` (that field alone drives the S3 migration out of the
// public prefix), while a RESTRICTED origin must send `visibility:
// "internal"` — sending `isInternal: true` there would PRESERVE restricted
// rather than clear it (see patch.ts), and only the visibility field's path
// checks for live grants before allowing the transition.
function openMakeInternalModal(p: Prototype) {
  const vis = p.visibility ?? (p.isInternal ? "internal" : "public");
  const fromRestricted = vis === "restricted";
  const overlay = document.createElement("div");
  overlay.className = "modal-overlay";
  const body = fromRestricted
    ? `<p style="margin: 8px 0; font-size: 0.875rem; line-height: 1.5; color: var(--loop-neutral-800);">Internal-only means only Rewinders can open it &mdash; no more access links. If any links are still active, you'll be asked to revoke them first.</p>`
    : `<p style="margin: 8px 0; font-size: 0.875rem; line-height: 1.5; color: var(--loop-neutral-800);">Once internal, this prototype can never be made public again. The viewer URL will redirect anyone without a rewind.io/rewind.com Google account.</p>
       <p style="margin: 8px 0; font-size: 0.875rem; line-height: 1.5; color: var(--loop-neutral-800);">Existing copies of the link will keep working but will require sign-in.</p>`;
  overlay.innerHTML = `
    <div class="modal make-internal-modal" role="dialog" aria-labelledby="mi-title">
      <div class="modal-content" style="padding: 24px;">
        <h2 id="mi-title" style="margin: 0 0 12px; font-size: 1rem;">Make "${escape(p.title)}" internal-only?</h2>
        ${body}
        <div class="modal-actions">
          <button class="btn-secondary btn-cancel">Cancel</button>
          <button class="btn-primary btn-confirm">Make internal</button>
        </div>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  const close = () => overlay.remove();
  overlay.querySelector(".btn-cancel")!.addEventListener("click", close);
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) close();
  });

  overlay.querySelector(".btn-confirm")!.addEventListener("click", () => attemptMakeInternal(p, fromRestricted, overlay));
}

async function attemptMakeInternal(p: Prototype, fromRestricted: boolean, overlay: HTMLElement): Promise<void> {
  const confirmBtn = overlay.querySelector<HTMLButtonElement>(".btn-confirm")!;
  confirmBtn.disabled = true;
  confirmBtn.textContent = "Working…";
  try {
    await api.patch(p.slug, fromRestricted ? { visibility: "internal" } : { isInternal: true });
    overlay.remove();
    await updateCard(p.slug);
  } catch (e) {
    const err = e as ApiError;
    if (fromRestricted && err.status === 409 && err.body?.error === "grants exist" && typeof err.body.count === "number") {
      overlay.remove();
      await openRevokeGrantsFirstModal(p, err.body.count as number);
      return;
    }
    confirmBtn.disabled = false;
    confirmBtn.textContent = "Make internal";
    alert(`Failed: ${err.message}`);
  }
}

// 409 "grants exist" on a visibility change away from restricted: the
// portal's access-links band is conditional on `restricted`, so leaving it
// with live grants would orphan working credentials with no owner-visible
// UI. The remedy is a bulk revoke followed by a retry of the SAME change.
export async function openRevokeGrantsFirstModal(p: Prototype, count: number): Promise<void> {
  let grants: Grant[] = [];
  let loadFailed = false;
  try {
    grants = await listGrants(p.slug);
  } catch {
    // If the list itself can't be fetched, disable the bulk-revoke action
    // rather than letting it silently no-op (an empty `grants` array would
    // otherwise skip every revoke and retry the same change, which would
    // fail with the identical 409 — a confusing "Revoking…" -> generic
    // failure with no indication that nothing was actually revoked).
    loadFailed = true;
  }
  // Every rendered count comes from the list actually fetched, not from the
  // 409 body's `count`. Under a race with another revoke the two disagree, and
  // the dialog would then label its button "Revoke all 3" above a list of 2 —
  // promising an action over a number it isn't holding. `count` survives only
  // as the fallback for when the list couldn't be loaded at all, where the
  // confirm button is disabled anyway.
  const n = loadFailed ? count : grants.length;
  const confirmLabel = `Revoke all ${n} and make internal`;

  await openRevokeGrantsThenModal({
    kind: "revoke-first-modal",
    titleId: "rf-title",
    title: "Revoke the access links first",
    lead: `"${escape(p.title)}" has ${n} active access link${n === 1 ? "" : "s"}. Making it internal-only would leave ${n === 1 ? "it" : "them"} working for anyone who already has the link, with no way to see or manage it from here.`,
    closing:
      "Revoking takes effect <b>within a few seconds</b> and cannot be undone. If you need to share again later, re-issuing a new link only takes <b>one click</b> from the Manage panel.",
    cancelLabel: "Cancel",
    confirmLabel,
    grants,
    loadFailed,
    slug: p.slug,
    // The field choice stays HERE, inside the function
    // tests/ui-visibility.test.ts scopes its assertion to. patch.ts refuses
    // the legacy boolean field for a restricted origin (it means "internal,
    // preserving restricted if already set", so it would silently no-op), and
    // hoisting this literal into the shared builder below would move it outside
    // the asserted region and quietly weaken a real test.
    //
    // Don't name that boolean field verbatim in prose anywhere in this
    // function: the test asserts its absence by source-text match, so a comment
    // mentioning it fails the assertion.
    then: () => api.patch(p.slug, { visibility: "internal" }),
  });
}

// The shared chrome behind both revoke-then-retry dialogs: overlay, modal box,
// the list of link labels, the load-failure notice, the two buttons, the
// dismissal wiring, and the confirm handler's revoke-each-then-act sequence
// with its button-restoring error path.
//
// Only the CHROME is shared. Every string a user reads is supplied by the
// caller, and so is the action taken after the revokes. That split is
// deliberate: the two refusals have the same remedy but very different stakes
// — leaving restricted orphans links that keep working invisibly, while
// re-arming expiry schedules the content's deletion — and the copy is the whole
// value of having two dialogs. What was duplicated was ~40 lines of markup and
// wiring, which is worth having once; the wording is not.
async function openRevokeGrantsThenModal(opts: {
  kind: string;
  titleId: string;
  title: string;
  lead: string;
  closing: string;
  cancelLabel: string;
  confirmLabel: string;
  grants: Grant[];
  loadFailed: boolean;
  slug: string;
  then: () => Promise<unknown>;
}): Promise<void> {
  const overlay = document.createElement("div");
  overlay.className = "modal-overlay";
  const list = opts.grants.length
    ? `<ul style="margin: 8px 0; padding-left: 20px; font-size: 0.875rem; color: var(--loop-neutral-800);">
        ${opts.grants.map((g) => `<li>${escape(g.label)}</li>`).join("")}
      </ul>`
    : "";
  const loadFailedNotice = opts.loadFailed
    ? `<p class="error" style="margin: 8px 0; font-size: 0.875rem;">Couldn't load the current links. Close this and try again.</p>`
    : "";
  overlay.innerHTML = `
    <div class="modal ${opts.kind}" role="dialog" aria-labelledby="${opts.titleId}">
      <div class="modal-content" style="padding: 24px;">
        <h2 id="${opts.titleId}" style="margin: 0 0 12px; font-size: 1rem;">${opts.title}</h2>
        <p style="margin: 8px 0; font-size: 0.875rem; line-height: 1.5; color: var(--loop-neutral-800);">
          ${opts.lead}
        </p>
        ${list}
        ${loadFailedNotice}
        <p style="margin: 8px 0; font-size: 0.875rem; line-height: 1.5; color: var(--loop-neutral-800);">
          ${opts.closing}
        </p>
        <div class="modal-actions">
          <button class="btn-secondary btn-cancel">${opts.cancelLabel}</button>
          <button class="btn-primary btn-confirm" ${opts.loadFailed ? "disabled" : ""}>${opts.confirmLabel}</button>
        </div>
      </div>
    </div>`;
  document.body.appendChild(overlay);

  const close = () => overlay.remove();
  overlay.querySelector(".btn-cancel")!.addEventListener("click", close);
  overlay.addEventListener("click", (e) => { if (e.target === overlay) close(); });

  if (opts.loadFailed) return;

  overlay.querySelector(".btn-confirm")!.addEventListener("click", async () => {
    const btn = overlay.querySelector<HTMLButtonElement>(".btn-confirm")!;
    btn.disabled = true;
    btn.textContent = "Revoking…";
    try {
      for (const g of opts.grants) {
        await revokeGrant(opts.slug, g.id).catch((e) => {
          // 404 here means the grant is already gone (revoked elsewhere, or
          // never belonged to this slug) — not a reason to abort the rest of
          // the bulk revoke.
          if ((e as Error).message !== "not found") throw e;
        });
      }
      await opts.then();
      close();
      await updateCard(opts.slug);
    } catch (err) {
      btn.disabled = false;
      btn.textContent = opts.confirmLabel;
      alert(`Failed: ${(err as Error).message}`);
    }
  });
}

// 409 "grants exist" from unkeep.ts. Deliberately a separate dialog from the
// visibility one above rather than a shared parameterised builder: the two
// refusals have the same remedy but completely different stakes, and the copy
// is the whole value here. Leaving restricted orphans links that keep working
// invisibly; re-arming expiry instead schedules the content's deletion, so this
// dialog has to name the 90 days and say the links die with the files.
export async function openRevokeGrantsBeforeExpiryModal(p: Prototype, count: number): Promise<void> {
  let grants: Grant[] = [];
  let loadFailed = false;
  try {
    grants = await listGrants(p.slug);
  } catch {
    // Same reasoning as the visibility dialog: an empty list would silently
    // revoke nothing and retry into the identical 409.
    loadFailed = true;
  }
  // See the note in openRevokeGrantsFirstModal: the fetched length, not the
  // 409 body's count.
  const n = loadFailed ? count : grants.length;

  await openRevokeGrantsThenModal({
    kind: "revoke-before-expiry-modal",
    titleId: "rx-title",
    title: "These links would expire with it",
    lead: `"${escape(p.title)}" has ${n} active access link${n === 1 ? "" : "s"}. Letting it expire schedules its files for deletion in 90 days, and ${n === 1 ? "that link" : "those links"} will break for good on the day it happens &mdash; with no warning to whoever you sent ${n === 1 ? "it" : "them"} to.`,
    closing:
      'Revoking now takes effect <b>within a few seconds</b> and is honest &mdash; the recipient gets a clear "this link was revoked" page instead of a link that quietly dies later. If you need to share again, re-issuing is <b>one click</b> from this panel.',
    cancelLabel: "Keep it permanently",
    confirmLabel: `Revoke all ${n} and let it expire`,
    grants,
    loadFailed,
    slug: p.slug,
    then: () => api.unkeep(p.slug),
  });
}
